import BreadCrumb from "Common/BreadCrumb";
import withRouter from "Common/withRouter";
import { useEffect, useState } from "react";
import { useSync } from "../../context/SyncContext";
import { RefreshCw } from "lucide-react";

interface AuthUserData {
  documentOwner: string;
  id: number;
  name: string;
  user: string;
  password: string;
  lastSyncDate: string;
}

const UserProfile = () => {
  const [userData, setUserData] = useState<AuthUserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { syncState, syncNow } = useSync();

  useEffect(() => {
    const loadUserData = () => {
      try {
        const authUserData = localStorage.getItem("authUser");

        if (authUserData) {
          const parsedData: AuthUserData = JSON.parse(authUserData);

          setUserData(parsedData);
        } else {
          setUserData(null);
        }
      } catch (error) {
        console.error("❌ Erro ao fazer parse dos dados do usuário:", error);
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  if (loading) {
    return (
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <BreadCrumb title="Perfil" pageTitle="Perfil" />
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-center">
            <div className="w-8 h-8 mx-auto border-b-2 border-blue-600 rounded-full animate-spin"></div>
            <p className="mt-2 text-gray-600">Carregando dados do perfil...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
      <BreadCrumb title="Perfil" pageTitle="Perfil" />

      <div className="row">
        <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-1">
          <div className="card">
            <div className="card-body">
              {userData ? (
                <div className="flex gap-3">
                  <div className="text-slate-500 dark:text-zink-200">
                    <div className="flex gap-2">
                      <div className="grow">
                        <h6 className="text-slate-500">Nome</h6>
                        <p className="mb-1">{userData.name ?? "Nome não informado"}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <div className="grow">
                        <h6 className="text-slate-500">Usuário</h6>
                        <p className="mb-1">{userData.user ?? "Usuário não informado"}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <div className="grow">
                        <h6 className="text-slate-500">Documento do Proprietário</h6>
                        <p className="mb-1">{userData.documentOwner ?? "Não informado"}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <div className="grow">
                        <h6 className="text-slate-500">ID</h6>
                        <p className="mb-1">{userData.id ?? "ID não informado"}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <div className="grow">
                        <h6 className="text-slate-500">Último Sincronismo</h6>
                        <div className="flex items-center gap-2">
                          <p className="mb-1">
                            {syncState.lastSyncAt
                              ? new Date(syncState.lastSyncAt).toLocaleString("pt-BR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : userData.lastSyncDate
                              ? new Date(userData.lastSyncDate).toLocaleString("pt-BR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "Nunca sincronizado"}
                          </p>
                          <button
                            onClick={syncNow}
                            disabled={syncState.isSyncing}
                            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-zink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Sincronizar agora"
                          >
                            <RefreshCw
                              className={`size-4 text-custom-500 ${
                                syncState.isSyncing ? "animate-spin" : ""
                              }`}
                            />
                          </button>
                        </div>
                        {syncState.pendingCount > 0 && (
                          <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                            {syncState.pendingCount} pedido{syncState.pendingCount > 1 ? "s" : ""}{" "}
                            pendente
                            {syncState.pendingCount > 1 ? "s" : ""}
                          </p>
                        )}
                        {syncState.syncError && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            Erro: {syncState.syncError}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-slate-500 dark:text-zink-200">
                  <p>Dados do usuário não encontrados.</p>
                  <p className="mt-2 text-sm">Faça login novamente para carregar os dados.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(UserProfile);
